import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Loader } from "@/components/ui/Loader";
import { ROUTES } from "@/config/constants";
import { cn } from "@/utils/cn";
import {
  extractGgError,
  fetchPesquisaDetalhe,
  fetchResultados,
  setStatusPesquisaApi,
  updatePesquisaApi,
  type GgColaboradorDto,
  type GgConviteDto,
  type GgPerguntaDto,
  type GgPesquisaDetalheDto,
  type GgResultadosDto,
} from "@/services/genteGestao";
import { AbaPerguntas } from "./AbaPerguntas";
import { AbaParticipantes } from "./AbaParticipantes";
import { AbaResultados } from "./AbaResultados";

type Aba = "perguntas" | "participantes" | "resultados";

export function GenteGestaoNpsDetalhePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [aba, setAba] = useState<Aba>("participantes");
  const [pesquisa, setPesquisa] = useState<GgPesquisaDetalheDto | null>(null);
  const [perguntas, setPerguntas] = useState<GgPerguntaDto[]>([]);
  const [convites, setConvites] = useState<GgConviteDto[]>([]);
  const [ativos, setAtivos] = useState<GgColaboradorDto[]>([]);
  const [resultados, setResultados] = useState<GgResultadosDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const det = await fetchPesquisaDetalhe(id);
      setPesquisa(det.pesquisa);
      setPerguntas(det.perguntas);
      setConvites(det.convites);
      setAtivos(det.colaboradoresAtivos);
      if (det.pesquisa.respostas > 0) {
        setResultados(await fetchResultados(id));
      } else {
        setResultados(null);
      }
    } catch (e) {
      setError(extractGgError(e, "Não foi possível carregar a pesquisa."));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading && !pesquisa) {
    return (
      <div className="flex justify-center py-16">
        <Loader label="Carregando pesquisa..." />
      </div>
    );
  }
  if (error || !pesquisa) {
    return <p className="text-sm text-red-600">{error ?? "Pesquisa não encontrada."}</p>;
  }

  const current = pesquisa;

  async function salvarMeta() {
    setSaving(true);
    try {
      await updatePesquisaApi(current.id, {
        titulo: current.titulo,
        descricao: current.descricao,
        nomeEmpresa: current.nomeEmpresa,
        dataInicio: current.dataInicio,
        dataFim: current.dataFim,
        mensagemConvite: current.mensagemConvite,
        permiteLinkGeral: current.permiteLinkGeral,
        modoPrivacidade: current.modoPrivacidade,
      });
      await load();
    } catch (e) {
      setError(extractGgError(e, "Não foi possível salvar."));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <button className="text-sm text-brand-700 hover:underline" onClick={() => navigate(ROUTES.GENTE_GESTAO_NPS)}>
          ← Pesquisas
        </button>
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={cn(
              "rounded-full px-2.5 py-1 text-xs font-semibold",
              pesquisa.status === "ATIVA"
                ? "bg-emerald-50 text-emerald-800"
                : pesquisa.status === "ENCERRADA"
                  ? "bg-slate-100 text-slate-600"
                  : "bg-amber-50 text-amber-800",
            )}
          >
            {pesquisa.status === "ATIVA" ? "Ativa" : pesquisa.status === "ENCERRADA" ? "Encerrada" : "Rascunho"}
          </span>
          {pesquisa.status !== "ATIVA" && (
            <Button size="sm" onClick={() => void setStatusPesquisaApi(pesquisa.id, "ATIVA").then(load)}>
              Ativar pesquisa
            </Button>
          )}
          {pesquisa.status === "ATIVA" && (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => void setStatusPesquisaApi(pesquisa.id, "ENCERRADA").then(load)}
            >
              Encerrar
            </Button>
          )}
          {pesquisa.status === "ENCERRADA" && (
            <Button size="sm" variant="secondary" onClick={() => void setStatusPesquisaApi(pesquisa.id, "ATIVA").then(load)}>
              Reabrir
            </Button>
          )}
        </div>
      </div>

      <div className="grid gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-card sm:grid-cols-2">
        <Input
          label="Título"
          value={current.titulo}
          onChange={(e) => setPesquisa({ ...current, titulo: e.target.value })}
        />
        <Input
          label="Empresa"
          value={current.nomeEmpresa}
          onChange={(e) => setPesquisa({ ...current, nomeEmpresa: e.target.value })}
        />
        <Input
          label="Início"
          type="date"
          value={current.dataInicio?.slice(0, 10) ?? ""}
          onChange={(e) => setPesquisa({ ...current, dataInicio: e.target.value || null })}
        />
        <Input
          label="Fim"
          type="date"
          value={current.dataFim?.slice(0, 10) ?? ""}
          onChange={(e) => setPesquisa({ ...current, dataFim: e.target.value || null })}
        />
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={current.permiteLinkGeral}
            onChange={(e) => setPesquisa({ ...current, permiteLinkGeral: e.target.checked })}
          />
          Permitir link geral
        </label>
        <Button size="sm" variant="secondary" loading={saving} onClick={() => void salvarMeta()}>
          Salvar dados
        </Button>
      </div>

      <div className="flex flex-wrap gap-2">
        {(
          [
            ["perguntas", "Perguntas"],
            ["participantes", "Participantes e envio"],
            ["resultados", "Resultados"],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setAba(key)}
            className={cn(
              "rounded-lg px-3 py-2 text-sm font-medium",
              aba === key ? "bg-brand-600 text-white" : "bg-white border border-slate-200 text-slate-600",
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {aba === "perguntas" && (
        <AbaPerguntas
          pesquisaId={pesquisa.id}
          perguntas={perguntas}
          bloqueado={pesquisa.perguntasBloqueadas}
          onChanged={() => void load()}
        />
      )}
      {aba === "participantes" && (
        <AbaParticipantes
          pesquisa={pesquisa}
          convites={convites}
          colaboradoresAtivos={ativos}
          onChanged={() => void load()}
        />
      )}
      {aba === "resultados" &&
        (resultados ? (
          <AbaResultados resultados={resultados} />
        ) : (
          <p className="text-sm text-slate-500">Ainda não há respostas para calcular os resultados.</p>
        ))}
    </div>
  );
}
